import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseUrl } from "../../helpers/constants";
import { getCookie, signout } from "../../helpers/cookies";
import { warrantyParser } from "./warrantyParser";

const customFetchBaseQuery = fetchBaseQuery({
    baseUrl: baseUrl,
    prepareHeaders: (headers, { getState }) => {
        const token = getCookie("magiclock_admin_token");
        if (token) {
            headers.set("Authorization", `BEARER ${token}`);
        }
        return headers;
    },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
    let result = await customFetchBaseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 403) {
        signout(() => {
            window.location.href = "/login";
        });
    }

    return result;
};

const warrantyApis = createApi({
    reducerPath: "warrantyApis",
    baseQuery: baseQueryWithReauth,
    keepUnusedDataFor: 0,
    tagTypes: ["WarrantyRegistrations", "WarrantyDetails", "InsuranceDetails"],
    endpoints: (build) => ({
        // Admin warranty registrations
        getAdminWarrantyRegistrations: build.query({
            query: (params) => {
                // Remove unsupported parameters
                const { sort_by, sort_order, search, ...validParams } = params;
                return {
                    url: "/common/warranty-registrations",
                    method: "GET",
                    params: validParams,
                };
            },
            transformResponse: (response) => warrantyParser(response),
            providesTags: ["WarrantyRegistrations"],
            keepUnusedDataFor: 5,
        }),
        getAdminWarrantyDetails: build.query({
            query: (id) => ({
                url: `/common/warranty-registration/${id}`,
                method: "GET",
            }),
            transformResponse: (response) => response.result?.warranty_registration || response.result,
            providesTags: (result, error, id) => [{ type: "WarrantyDetails", id }],
            keepUnusedDataFor: 5,
        }),
        updateWarrantyStatus: build.mutation({
            query: (payload) => ({
                url: `/common/warranty-registration/${payload.id}/status`,
                method: "PATCH",
                body: {
                    status: payload.status,
                    approved_by: payload.approved_by,
                    rejection_reason: payload.rejection_reason,
                },
            }),
            transformResponse: (response) => response.result,
            invalidatesTags: ["WarrantyRegistrations", "WarrantyDetails"],
        }),
        getInsuranceDetails: build.query({
            query: (id) => ({
                url: `/common/insurance/${id}`,
                method: "GET",
            }),
            transformResponse: (response) => response.result?.insurance || response.result,
            providesTags: (result, error, id) => [{ type: "InsuranceDetails", id }],
            keepUnusedDataFor: 5,
        }),
    }),
});

export const {
    useGetAdminWarrantyRegistrationsQuery,
    useGetAdminWarrantyDetailsQuery,
    useUpdateWarrantyStatusMutation,
    useGetInsuranceDetailsQuery,
} = warrantyApis;
export default warrantyApis;
